import { ExtractComponentProp } from '@approvalmax/types';
import { DoubleChevronUpIcon } from '@approvalmax/ui';
import { type BodyCellProps, ConditionWrapper, Flex, Select, Table, Text } from '@approvalmax/ui/src/components';
import { selectors } from 'modules/common';
import { domain } from 'modules/data';
import { CompanyUserRole } from 'modules/data/domain';
import { useDispatch, useSelector } from 'modules/react-redux';
import { FC, memo, useCallback, useMemo } from 'react';
import { useToggle } from 'react-use';

import { changeUserRole } from '../../../../actions';
import type { UserRow } from '../../UserList.types';
import { messages } from './RoleCell.messages';

const RoleCell: FC<BodyCellProps<UserRow>> = memo((props) => {
    const { hovered, item } = props;

    const [open, toggleOpen] = useToggle(false);

    const company = useSelector(selectors.navigation.getActiveCompany);
    const me = useSelector(selectors.profile.getProfileUser);
    const canChangeTeamMembersRoles = useSelector((state) =>
        selectors.company.canChangeTeamMembersRoles(state, company, me)
    );

    const dispatch = useDispatch();

    const roleItems = useMemo(
        () =>
            [
                domain.CompanyUserRole.Participant,
                domain.CompanyUserRole.Auditor,
                domain.CompanyUserRole.Manager,
                domain.CompanyUserRole.WorkflowManager,
            ].map((role) => ({
                id: role,
                name: selectors.company.getCompanyUserRoleText(role),
            })),
        []
    );

    const roleName = useMemo(() => selectors.company.getCompanyUserRoleText(item.role), [item.role]);

    const isSuperAdmin = item.id === company.author;
    const disabled = !canChangeTeamMembersRoles(item.id);

    const onSelectChange = useCallback<ExtractComponentProp<typeof Select, 'onChange'>>(
        (value) => {
            dispatch(changeUserRole(company.id, item, value as CompanyUserRole));
        },
        [company.id, dispatch, item]
    );

    const onOpenHandle = useCallback(() => {
        toggleOpen();
    }, [toggleOpen]);

    const inactive = item.status !== domain.CompanyUserStatus.Active;

    return (
        <Table.Cell {...props} columnSpacing='8'>
            {isSuperAdmin || disabled ? (
                <ConditionWrapper
                    condition={isSuperAdmin}
                    wrapperIf={(children) => (
                        <Flex alignItems='center' wrap={false}>
                            {children}

                            <DoubleChevronUpIcon size={12} />
                        </Flex>
                    )}
                >
                    <Text
                        color={inactive ? 'midnight60' : 'black100'}
                        fontWeight={isSuperAdmin ? 'bold' : 'regular'}
                        font='body'
                        fontSize='small'
                    >
                        {isSuperAdmin ? messages.superAdminRoleText : roleName}
                    </Text>
                </ConditionWrapper>
            ) : (
                <Select
                    size='small'
                    hideEndIcon={!(open || hovered)}
                    clearable={false}
                    onOpen={onOpenHandle}
                    onChange={onSelectChange}
                    disabled={disabled}
                    bordered={open || hovered}
                    items={roleItems}
                    value={item.role}
                />
            )}
        </Table.Cell>
    );
});

RoleCell.displayName = 'RoleCell';

export default RoleCell;
